import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { BaseListing } from '../_base-listing/base-listing'
import { ListingItem } from 'depoto-core/src/models'
import { BaseListingComponent } from '../../components/_common/base-listing/base-listing.component'
import { CoreService } from '../../services'
import { Checkout, Currency, Depot, ProductMove, PurchaseOrder, Supplier, Tag } from 'depoto-core/src/entities'
import { purchaseOrdersColumns } from '../../utils/definitions.util'
import { SchemaUtil } from '../../utils'
import { Router } from '@angular/router'

type ExtendedPurchaseOrder = PurchaseOrder & {
  currency: {
    id: string
    name: string
    ratio: number
  }
}

const DEFAULT_FILTERS = {
  visible: false,
  expanded: false,
  status: 'all',
  showFulfilledOnly: 'false',
  depot: { id: 0, name: 'all' },
  payments: [],
  dateCreatedFrom: '',
  dateCreatedTo: '',
  supplier: { id: 0, name: 'all' },
  dateEstimatedArrivalFrom: '',
  dateEstimatedArrivalTo: '',
  fulltext: '',
  tags: [],
  sortProp: 'created',
  sortDir: 'desc',
}

@Component({
  selector: 'app-purchase-orders',
  templateUrl: './purchase-orders.component.html',
  styleUrls: ['./purchase-orders.component.scss'],
})
export class PurchaseOrdersComponent extends BaseListing implements OnInit, OnDestroy {
  @ViewChild('baseListingComponent', { static: true }) baseListingComponent: BaseListingComponent
  listingColumns: ListingItem[] = purchaseOrdersColumns
  purchaseOrders: ExtendedPurchaseOrder[] = []
  depots: Partial<Depot>[] = []
  checkouts: Checkout[] = []
  tags: Tag[] = []
  totalItems = 0
  currentPage: number
  endPage: number
  filters = JSON.parse(JSON.stringify(DEFAULT_FILTERS))
  // filters = DEFAULT_FILTERS
  suppliers: Supplier[] = []
  statusOptions = [
    { id: 'all', name: 'all' },
    { id: 'new', name: 'Na cestě' },
    { id: 'partly_stocked', name: 'Částečně naskladněno' },
    { id: 'stocked', name: 'Zcela naskladněno' },
    { id: 'cancelled', name: 'Stornováno' },
  ]

  canCreate = false
  loading = true
  subscription
  queryFilters
  storageKey: { type: string; key: string }
  userHavePermissionForThisPage = false
  companyCurrency: Currency
  hasRoleExportProductsForMetrics: boolean

  constructor(
    protected core: CoreService,
    private router: Router,
  ) {
    super(core)
    this.getStorageKey('purchaseOrders')
    this.userHavePermissionForThisPage = this.core.services.user?.user?.company?.moduleRelations?.some(
      moduleRelation => moduleRelation.module.id === 'purchase-orders',
    )
    this.companyCurrency = this.core.services.user?.user?.company?.currency
  }

  async ngOnInit() {
    this.core.setPurchaseOrderInProgress(null)
    if (!this.userHavePermissionForThisPage) {
      this.loading = false
    } else {
      this.filters = this.loadViewSettings('purchaseOrders') || JSON.parse(JSON.stringify(DEFAULT_FILTERS))
      this.sortProp = this.filters.sortProp
      this.sortDir = this.filters.sortDir
      await this.refreshFilterData()
      this.canCreate =
        this.core.services.user.hasRole('ROLE_PURCHASE_ORDER_CREATE') ||
        this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
        this.core.services.user.hasRole('ROLE_ADMIN')
      this.hasRoleExportProductsForMetrics =
        this.core.services.user.hasRole('ROLE_EXPORT_PRODUCTS_FOR_METRICS') ||
        this.core.services.user.hasRole('ROLE_ADMIN') ||
        this.core.services.user.hasRole('ROLE_SUPER_ADMIN')
      this.filter()
    }
  }

  async ngOnDestroy() {
    this.subscription?.unsubscribe()
  }

  async refreshFilterData() {
    try {
      this.suppliers = [{ id: 0, name: 'all', externalId: undefined }, ...this.core.lists.suppliers]
      this.depots = [
        { id: 0, name: 'all' },
        ...this.core.lists.depots.filter(depot => {
          return depot.name !== 'all' && this.core.services.user?.user?.depots?.some(d => d.id === depot.id)
        }),
      ]
      this.tags = this.core.lists.tags.filter(tag => tag.type === 'purchaseOrder')
      this.statusOptions = [
        { id: 'all', name: 'all' },
        ...this.core.lists.purchaseOrderStatuses.map(s => ({ id: s.id, name: `purchaseOrderStatus.${s.id}` })),
      ]
      this.loading = true
    } finally {
      this.loading = false
    }
  }

  getQueryFilters() {
    this.queryFilters = {
      depot: this.filters.depot?.id || undefined,
      supplier: this.filters.supplier?.id || undefined,
      fulltext: this.filters.fulltext?.length ? this.filters.fulltext.trim() : undefined,
      status: this.filters.status?.length ? this.filters.status.trim() : undefined,
      tags: this.filters.tags?.length ? this.filters?.tags : undefined,
    }
    this.queryFilters.showStocked = this.filters.showFulfilledOnly === 'true'
    Object.keys(this.queryFilters).forEach(k => {
      if (this.queryFilters[k] === undefined) {
        delete this.queryFilters[k]
      }
    })
    if (this.filters.dateCreatedFrom?.length > 0 || this.filters.dateCreatedTo?.length > 0) {
      this.queryFilters.created = {
        ...(this.filters.dateCreatedFrom?.length && {
          left_date: this.filters.dateCreatedFrom,
        }),
        ...(this.filters.dateCreatedTo?.length && {
          right_date: this.filters.dateCreatedTo + 'T23:59:59',
        }),
      }
    }

    if (this.filters.dateEstimatedArrivalFrom?.length > 0 || this.filters.dateEstimatedArrivalTo?.length > 0) {
      this.queryFilters.dateEstimatedArrival = {
        ...(this.filters.dateEstimatedArrivalFrom?.length && {
          left_date: this.filters.dateEstimatedArrivalFrom,
        }),
        ...(this.filters.dateEstimatedArrivalTo?.length && {
          right_date: this.filters.dateEstimatedArrivalTo + 'T23:59:59',
        }),
      }
    }
  }

  async filter(
    pageNo = 1,
    sortProp: string = this.filters.sortProp,
    sortDir: string = this.filters.sortDir,
    concat = false,
  ) {
    this.getQueryFilters()
    this.loading = true
    this.currentPage = pageNo
    if (!concat) {
      this.reloading = true
    }

    try {
      const result = await this.core.services.purchase.getList(
        {
          page: pageNo,
          sort: sortProp,
          direction: sortDir,
          filters: {
            ...this.queryFilters,
          },
        },
        SchemaUtil.purchaseOrder,
      )
      if (result === undefined) {
        return
      }
      if (!concat) {
        this.purchaseOrders = []
      }
      result?.items.forEach((po: PurchaseOrder) => {
        const epo: ExtendedPurchaseOrder = { ...new PurchaseOrder(po), currency: this.companyCurrency }
        this.purchaseOrders.push(epo)
      })
      this.totalItems = result.paginator?.totalCount ? result.paginator?.totalCount : 0
      this.endPage = result.paginator?.endPage ? result.paginator?.endPage : 0
      this.saveViewSettings('purchaseOrders', this.filters)
      this.loading = false
      this.reloading = false
    } catch (err) {
      console.error(err)
      this.loading = false
      this.reloading = false
    }
  }

  async stockUpOrder(order?: PurchaseOrder) {
    const res = await this.core.services.purchase.getById(order.id, SchemaUtil.purchaseOrderWithItemsOnly)
    if (res?.items?.length) {
      order.items = res.items
    } else {
      return
    }
    const operations = []
    order.items.forEach(item => {
      if (item.quantity - item.quantityIn > 0) {
        operations.push({
          // new ProductMove({ // this is, for some fucking unknown shitty reason, messing up whole object. but just on THIS page.. on detail and single item list is ok. idk. fuck it. fakin hell
          amount: item.quantity - item.quantityIn,
          product: {
            id: item.product.id,
            name: item.product.fullName,
            fullName: item.product.fullName,
            purchaseCurrency: item.currency,
            mainImage: item.product.mainImage,
            enabledBatchExpiration: item.product.enabledBatchExpiration,
            tags: item.product.tags,
            packs: item.product.packs,
          },
          purchasePrice: item.purchasePrice,
          purchaseOrderItem: item,
          supplier: order.supplier.id,
          // }),
        })
      }
    })
    const stockIn = {
      depotTo: order.depot.id,
      purchaseOrderId: order.id,
      operations,
    }
    this.router.navigate(['stock-batch-in'], { state: { stockIn } })
  }

  public clearFilters() {
    this.filters = JSON.parse(JSON.stringify(DEFAULT_FILTERS))
    this.filter(1)
  }

  openDetail(po: PurchaseOrder) {
    if (po.supplier?.externalId === 'henryschein') {
      return this.router.navigate(['/purchase-order-hs', po.id])
    } else {
      return this.router.navigate(['/purchase-order', po.id])
    }
  }
}
